import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ToolCard } from '@/components/common/ToolCard';
import { AdPlaceholder } from '@/components/common/AdPlaceholder';
import { Link } from 'react-router-dom';
import {
  Calculator,
  TrendingUp,
  CreditCard,
  Receipt,
  Briefcase,
  PiggyBank,
  BarChart3,
  Shield,
  Zap,
  Users,
  ArrowRight,
  Sparkles,
} from 'lucide-react';

const LandingPage = () => {
  const featuredTools = [
    {
      name: 'SIP Calculator',
      description: 'Calculate returns on your systematic investment plan with accurate projections.',
      category: 'Investment',
      icon: TrendingUp,
      href: '/tools/sip-calculator',
      tags: ['Investment', 'SIP', 'Mutual Funds'],
      popular: true,
    },
    {
      name: 'EMI Calculator',
      description: 'Compute your monthly loan installments instantly for any loan amount.',
      category: 'Loan & Credit',
      icon: CreditCard,
      href: '/tools/emi-calculator',
      tags: ['Loan', 'EMI', 'Finance'],
      popular: true,
    },
    {
      name: 'Income Tax Calculator',
      description: 'Calculate your tax liability and plan your tax-saving investments efficiently.',
      category: 'Tax & Salary',
      icon: Receipt,
      href: '/tools/income-tax-calculator',
      tags: ['Tax', 'Income', 'Planning'],
      popular: true,
    },
    {
      name: 'Budget Calculator',
      description: 'Create and manage your monthly budget to achieve your financial goals.',
      category: 'Personal Finance',
      icon: Calculator,
      href: '/tools/budget-calculator',
      tags: ['Budget', 'Planning', 'Savings'],
    },
    {
      name: 'ROI Calculator',
      description: 'Measure the profitability of your business investments and decisions.',
      category: 'Business',
      icon: Briefcase,
      href: '/tools/roi-calculator',
      tags: ['Business', 'ROI', 'Investment'],
    },
    {
      name: 'Compound Interest Calculator',
      description: 'See how your money grows over time with the power of compounding.',
      category: 'Investment',
      icon: TrendingUp,
      href: '/tools/compound-interest-calculator',
      tags: ['Investment', 'Growth', 'Savings'],
    },
  ];

  const categories = [
    {
      name: 'Personal Finance',
      description: 'Budget, savings, and wealth management tools',
      icon: PiggyBank,
      count: 5,
      color: 'text-chart-1',
      href: '/explore#personal-finance',
    },
    {
      name: 'Investment',
      description: 'SIP, mutual funds, and stock calculators',
      icon: TrendingUp,
      count: 6,
      color: 'text-chart-2',
      href: '/explore#investment',
    },
    {
      name: 'Loan & Credit',
      description: 'EMI, home loan, and credit calculators',
      icon: CreditCard,
      count: 6,
      color: 'text-chart-3',
      href: '/explore#loan-credit',
    },
    {
      name: 'Tax & Salary',
      description: 'Income tax, GST, and salary calculators',
      icon: Receipt,
      count: 5,
      color: 'text-chart-4',
      href: '/explore#tax-salary',
    },
    {
      name: 'Business',
      description: 'Profit, ROI, and business valuation tools',
      icon: Briefcase,
      count: 5,
      color: 'text-chart-5',
      href: '/explore#business',
    },
  ];

  const features = [
    {
      icon: Zap,
      title: 'Instant Calculations',
      description: 'Get accurate results in real-time with our optimized algorithms.',
    },
    {
      icon: Shield,
      title: 'Privacy Focused',
      description: 'All calculations happen in your browser. Your data never leaves your device.',
    },
    {
      icon: BarChart3,
      title: 'Detailed Reports',
      description: 'Visualize your financial data with comprehensive charts and breakdowns.',
    },
    {
      icon: Users,
      title: 'Expert Backed',
      description: 'Tools designed and verified by financial experts and CPAs.',
    },
  ];

  const stats = [
    { value: '27+', label: 'Finance Tools' },
    { value: '100K+', label: 'Calculations Done' },
    { value: '50K+', label: 'Happy Users' },
    { value: '99.9%', label: 'Accuracy Rate' },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="gradient-hero py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in">
              <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-primary/20">
                <Sparkles className="w-3 h-3 mr-1" />
                Free Financial Tools
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight">
                Master Your Finances with
                <span className="text-gradient-primary"> Smart Tools</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                Access 27+ powerful calculators and tools designed to help you make informed financial decisions.
                From budgeting to investments, we've got you covered.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="shadow-primary hover-lift">
                  <Link to="/explore">
                    Explore All Tools
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="hover-lift">
                  <Link to="/about">Learn More</Link>
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-6 pt-4">
                {stats.slice(0, 3).map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl font-bold text-primary">{stat.value}</div>
                    <div className="text-xs text-muted-foreground">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-primary opacity-20 blur-3xl rounded-full"></div>
                <img
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&h=600&fit=crop"
                  alt="Financial Planning"
                  className="relative rounded-2xl shadow-elegant hover-scale transition-smooth"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-muted/30 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-gradient-primary mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ad Placement */}
      <section className="py-6 bg-background">
        <div className="container mx-auto px-4">
          <AdPlaceholder size="leaderboard" position="inline" />
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4" variant="outline">
              Categories
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
              Explore Tools by Category
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Choose from our comprehensive collection of financial tools organized by category
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <Link key={index} to={category.href}>
                  <Card className="h-full hover-lift border-border hover:border-primary/50 hover:shadow-primary transition-smooth group">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-4">
                        <div
                          className={`flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 ${category.color} group-hover:bg-primary group-hover:text-primary-foreground transition-smooth`}
                        >
                          <Icon className="w-7 h-7" />
                        </div>
                        <Badge variant="secondary">{category.count} tools</Badge>
                      </div>
                      <CardTitle className="text-xl group-hover:text-primary transition-smooth">
                        {category.name}
                      </CardTitle>
                      <CardDescription>{category.description}</CardDescription>
                    </CardHeader>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Tools Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-4 gap-8">
            <div className="lg:col-span-3">
              <div className="text-center mb-12">
                <Badge className="mb-4" variant="outline">
                  Popular Tools
                </Badge>
                <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
                  Most Used Financial Calculators
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Start with our most popular tools trusted by thousands of users every day
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredTools.map((tool, index) => (
                  <ToolCard key={index} tool={tool} />
                ))}
              </div>
              <div className="text-center mt-12">
                <Button asChild size="lg" variant="outline" className="hover-lift">
                  <Link to="/explore">
                    View All 27 Tools
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
            <div className="lg:col-span-1 space-y-6">
              <AdPlaceholder size="medium" position="sidebar" />
              <AdPlaceholder size="medium" position="sidebar" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4" variant="outline">
              Why Choose Us
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
              Built for Your Financial Success
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our tools are designed with accuracy, privacy, and ease of use in mind
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="text-center hover-lift transition-smooth">
                  <CardHeader>
                    <div className="flex justify-center mb-4">
                      <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary">
                        <Icon className="w-8 h-8" />
                      </div>
                    </div>
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                    <CardDescription>{feature.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ad Placement */}
      <section className="py-6 bg-muted/30">
        <div className="container mx-auto px-4">
          <AdPlaceholder size="banner" position="inline" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5">
        <div className="container mx-auto px-4">
          <Card className="border-2 border-primary/20 shadow-primary">
            <CardContent className="p-8 md:p-12 text-center">
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
                Ready to Take Control of Your Finances?
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Join thousands of users who are making smarter financial decisions with our free tools
              </p>
              <Button asChild size="lg" className="shadow-primary hover-lift">
                <Link to="/explore">
                  Get Started Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
